import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { IoChevronBack } from 'react-icons/io5'

import { Input, PasswordEyeInput } from '@/components/forms'
import { Button, Spinner, Card } from '@/components/ui'
import { useAuthStore } from '@/stores/useAuthStore'
import { ROUTES } from '@/constants/routes'
import { sanitizeEmail } from '@/lib/sanitize'
import { logger } from '@/lib/logger'
import type { LoginRequest, AuthResult } from '@/types/auth'

import authStyles from '../Auth.module.css'

export function Login() {
    const navigate = useNavigate()
    const { logIn, isLoading, isLogged } = useAuthStore()

    const [formData, setFormData] = useState<LoginRequest>({
        email: '',
        password: '',
    })

    useEffect(() => {
        if (isLogged) {
            navigate(ROUTES.home)
        }
    }, [isLogged, navigate])

    const handleFormDataChange = (key: string, value: string) => {
        setFormData((prevState: LoginRequest) => ({
            ...prevState,
            [key]: value,
        }))
    }

    const validateForm = (formData: LoginRequest) => {
        const { email, password } = formData

        if (!email || !password) {
            return { isValid: false, message: 'Por favor complete todos los campos' }
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(email)) {
            return { isValid: false, message: 'Por favor ingrese un correo electrónico válido' }
        }

        return { isValid: true, message: '' }
    }

    const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        const validation = validateForm(formData)
        if (!validation.isValid) {
            toast.error(validation.message)
            return
        }

        // Sanitize inputs before sending to API
        const sanitizedData: LoginRequest = {
            email: sanitizeEmail(formData.email),
            password: formData.password, // Don't sanitize passwords
        }

        try {
            const status: AuthResult = await logIn(sanitizedData, () => logger.info('2FA required'));

            if (status === 'success') {
                toast.success('Inicio de sesión exitoso')
                navigate(ROUTES.home)
            }

            if (status === 'confirm_email') {
                toast.success('Por favor confirme su correo electrónico')
                navigate(ROUTES.verifyEmail)
            }

            return
        } catch (error) {
            toast.error('Ocurrió un error al iniciar sesión')
            logger.error('Login error:', error)
        }
    }

    // const handleLoginWithGoogle = async () => {
    //     return
    // }

    return (
        <div className={authStyles.container}>
            <div className={authStyles.header}>
                <button
                    className={authStyles.backButton}
                    onClick={() => navigate(ROUTES.home)}
                    aria-label="Volver al inicio"
                >
                    <IoChevronBack /> Volver al Inicio
                </button>
            </div>
            <Card className={authStyles.card}>
                <h1 className={authStyles.title}>Bienvenido</h1>

                <form className={authStyles.form} onSubmit={handleFormSubmit}>
                    <Input
                        name="email"
                        value={formData.email}
                        setValue={(value) => handleFormDataChange('email', value)}
                        label="Correo Electrónico"
                        placeholder="Ingrese su correo"
                    />
                    <PasswordEyeInput
                        name="password"
                        value={formData.password}
                        setValue={(value) => handleFormDataChange('password', value)}
                        label="Contraseña"
                        placeholder="Ingrese su contraseña"
                    />

                    <Button
                        variant="primary"
                        size="md"
                        type="submit"
                        disabled={isLoading}
                    >
                        {isLoading ? <Spinner variant="secondary" size="sm" /> : 'Iniciar Sesión'}
                    </Button>
                </form>

                <div className={authStyles.footer}>
                    <button
                        className="text-btn"
                        onClick={() => navigate(ROUTES.forgotPassword)}
                    >
                        ¿Olvidó su contraseña?
                    </button>
                </div>

                <div className={authStyles.footer}>
                    <span>¿No tiene una cuenta?</span>
                    <button
                        className="text-btn"
                        onClick={() => navigate(ROUTES.register)}
                    >
                        <span className={authStyles.link}>Registrarse</span>
                    </button>
                </div>
            </Card>
        </div>
    )
}

